import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useUpdatedState} from 'helpers/hooks/utils';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import Box from 'components/atoms/Layout/Box/Box';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import Delete from '@mui/icons-material/Delete';
import IconField from 'components/molecules/Fields/IconField/IconField';
import IconButton from 'components/atoms/Buttons/IconButton/IconButton';
import Icon from 'components/atoms/Icons/Icon/Icon';
import StyledFieldMappingForm from 'components/organisms/Forms/FieldMappingForm/FieldMappingForm.styles';

const FieldMappingForm = React.forwardRef((props, ref) => {
  const {
    field,
    only,
    list,
    team,
    connection,
    fieldData,
    required,
    readOnly,
    disabled,
    removable,
    hideActions,
    error,
    errorFrom,
    errorTo,
    onChange,
    onError,
    onDelete,
    ...innerProps
  } = useComponentProps({
    elevation: 1,
    radius: 'round',
    ...props
  }, 'FieldMappingForm');

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const fieldMappingForm = useMemo(() => ({
    refs: {
      ref: innerRef,
      formRef
    },
    submit: (touch = true) => {
      return formRef.current?.submit(touch);
    },
    validate: (touch = true) => {
      return formRef.current?.validate(touch);
    },
    reset: (state) => {
      setIsDirty(false);
      setInternalState({});
      return formRef.current?.reset(state);
    }
  }), []);

  useImperativeHandle(ref, () => fieldMappingForm);

  const [isDirty, setIsDirty] = useState(false);
  const [internalState, setInternalState] = useState(field);
  const [internalValue] = useUpdatedState(field);

  const isNew = !utils.isDefined(internalValue.from?.id) || !utils.isDefined(internalValue.to?.id);

  useEffect(() => {
    if (required && error) {
      return utils.observeTimeout(() => {
        try {
          formRef.current?.validate(true);
        } catch (e) {
          /* SQUASH */
        }
      }, constants.debounce.minimal)
    }
  }, [required, error]);

  const fields = useMemo(() => {
    const fields = [];

    const compatibleFields = (f1, f2) => {
      const getType = (field) => {
        const typeMap = {
          [constants.fieldDataTypes.int]: 'numeric',
          [constants.fieldDataTypes.numeric]: 'numeric',
          [constants.fieldDataTypes.boolean]: 'boolean',
          [constants.fieldDataTypes.text]: 'text',
          [constants.fieldDataTypes.citext]: 'text',
          [constants.fieldDataTypes.date]: 'date',
          [constants.fieldDataTypes.timestamp]: 'timestamp',
          [constants.fieldDataTypes.jsonb]: 'jsonb',
          [constants.fieldDataTypes.url]: 'text'
        }
        return typeMap[field.type] ?? field.type;
      }

      const typeCompatible = (f1, f2) => {
        return (getType(f1) === getType(f2)) ||
          (['text', 'boolean'].includes(getType(f1)) && ['text', 'boolean'].includes(getType(f2))) ||
          (['text', 'date', 'timestamp'].includes(getType(f1)) && ['text', 'date', 'timestamp'].includes(getType(f2))) ||
          (['text', 'numeric'].includes(getType(f1)) && ['text', 'numeric'].includes(getType(f2))) ||
          (['text', 'jsonb'].includes(getType(f1)) && ['text', 'jsonb'].includes(getType(f2)));
      }

      const rendererCompatible = (f1, f2) => {
        const special = ['comments', 'persons', 'location'];
        return (f1.renderer === f2.renderer) ||
          (!special.includes(f1.renderer) && !special.includes(f2.renderer));
      }

      const multipleCompatible = (f1, f2) => {
        return (f1.multiple === f2.multiple) ||
          (f1.multiple && (f2.multiple || getType(f2) === 'text')) ||
          (f2.multiple && (f1.multiple || getType(f1) === 'text'));
      }

      return typeCompatible(f1, f2) && rendererCompatible(f1, f2) && multipleCompatible(f1, f2);
    }

    const connectionDef = constants.data.lookup('connections', connection?.type);
    const directions = constants.data.mappingDirections
      .filter((md) => md.connections.find((c) => c === connection?.type))
      .filter((md) => !internalValue.to || !internalValue.to.readOnly || md.value === constants.client.connection.mappingDirections.in)
      .filter((md) => !internalValue.from || !internalValue.from.readOnly || md.value === constants.client.connection.mappingDirections.out);

    fields.push({
      name: 'from',
      label: `${constants.basics.shortName} field`,
      placeholder: `Type a ${constants.basics.shortName} field name`,
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.field,
      validate: (value, testContext) => {
        const values = testContext?.from?.[0]?.value || {};
        if (value && value?.readOnly && values.to && values.to.readOnly) {
          return testContext.createError({message: 'Select a not read only field'});
        } else if (value && values.to && !compatibleFields(value, values.to)) {
          return testContext.createError({message: 'Select a compatible field'});
        } else {
          return true;
        }
      },
      formGroup: 'header',
      context: {
        teamId: team ? (team?.from?.id || 0) : null,
        collectionId: list?.from?.collectionId
      },
      options: 'internalFields',
      initial: internalValue.from,
      readOnly: readOnly,
      required: required || utils.isDefined(internalState.to?.id) || isDirty,
      disabled: disabled,
      FormFieldProps: {
        error: errorFrom ? 'Duplicate field' : null,
        variant: 'inlineLabel',
        size: 'smaller',
        hiddenLabel: true
      }
    });

    fields.push({
      name: 'direction',
      label: 'Direction',
      formGroup: 'header',
      type: constants.formFieldTypes.popper,
      validation: constants.formFieldValidationTypes.text,
      conversion: constants.formFieldConversionTypes.value,
      initial: internalValue?.direction ? internalValue?.direction :
        directions[0]?.value,
      readOnly: readOnly,
      required: true,
      disabled: disabled || directions.length === 1,
      FormFieldProps: {
        hiddenLabel: true,
        variant: 'inlineLabel',
        size: 'smallest',
        fullWidth: true,
        FormFieldComponent: IconField,
        FormFieldProps: {
          iconSet: directions,
          orientation: 'vertical',
          columns: 1,
          rows: 3,
          minWidth: 0,
          IconButtonProps: {
            variant: 'outlined',
            size: 'smaller',
            density: 'normal',
            color: 'text.primary'
          },
          IconProps: {
            size: 'smaller',
            color: 'text.primary'
          }
        },
        PopperProps: {
          ContextPopperProps: {
            density: 'dense',
            size: 'medium',
            placement: 'bottom',
            width: 50
          }
        },
        renderButton: (value, {ref, className, readOnly, disabled, onClick}) => {
          const directionDef = constants.data.lookup('mappingDirections', (value?.value ?? value));
          if (readOnly) {
            return <Icon size="smaller"
                         icon={directionDef?.icon}
                         color="secondary"
                         showTooltip={false}/>
          } else {
            return <IconButton ref={ref}
                               className={className}
                               variant="outlined"
                               size="smaller"
                               density="sparse"
                               color="secondary"
                               disabled={Boolean(disabled || readOnly)}
                               onClick={onClick}>
              <Icon size="smaller"
                    icon={directionDef?.icon}
                    showTooltip={false}/>
            </IconButton>
          }
        }
      }
    });

    fields.push({
      name: 'to',
      label: `${connectionDef.label} field`,
      placeholder: `Type ${connectionDef.vowelLabel ? 'an' : 'a'} ${connectionDef.label} field name`,
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.field,
      validate: (value, testContext) => {
        const values = testContext?.from?.[0]?.value || {};
        if (value && value?.readOnly && values.from && values.from.readOnly) {
          return testContext.createError({message: 'Select a not read only field'});
        } else if (value && values.from && !compatibleFields(value, values.from)) {
          return testContext.createError({message: 'Select a compatible field'});
        } else {
          return true;
        }
      },
      formGroup: 'header',
      context: {
        type: connection?.type,
        key: connection?.key,
        teamId: team ? (team?.to?.id || 0) : null,
        listId: list?.to?.id
      },
      options: 'externalFields',
      initial: internalValue.to,
      readOnly: readOnly,
      required: required || utils.isDefined(internalState.from?.id),
      disabled: disabled,
      FormFieldProps: {
        error: errorTo ? 'Duplicate field' : null,
        variant: 'inlineLabel',
        size: 'smaller',
        hiddenLabel: true
      }
    });

    return fields;
  }, [internalValue, internalState, connection, list, team, errorFrom, errorTo, readOnly, disabled, required, isDirty]);

  const onChangeEvent = useEffectEvent(onChange);
  const handleSubmit = useCallback((values, actions) => {
    values = {...values};

    const directions = constants.data.mappingDirections
      .filter((md) => md.connections.find((c) => c === connection?.type));

    if (values['from'] && values['from'].readOnly) {
      if (directions.find((md) => md.value === constants.client.connection.mappingDirections.out)) {
        values['direction'] = constants.client.connection.mappingDirections.out;
      }
    } else if (values['to'] && values['to'].readOnly) {
      if (directions.find((md) => md.value === constants.client.connection.mappingDirections.in)) {
        values['direction'] = constants.client.connection.mappingDirections.in;
      }
    }

    utils.asPromise(onChangeEvent)({...values})
      .finally(() => {
        actions.setSubmitting(false);
      });
  }, [connection?.type, onChangeEvent]);

  const handleChange = useCallback(() => {
    formRef.current?.submit(false);
  }, []);

  const handleError = useCallback((field, value) => {
    utils.asPromise(onChangeEvent)({...internalValue, [field.name]: value});
  }, [internalValue, onChangeEvent]);

  const handleChangeDirect = useCallback((e) => {
    const field = fields?.find((field) => field.name === e?.target?.name);
    const value = e?.target?.value;

    if (field) {
      if (field.name === 'from') {
        setInternalState((current) => {
          if (current.from?.id !== value?.id) {
            return {
              ...current,
              from: value
            }
          } else {
            return current;
          }
        });
      } else if (field.name === 'to') {
        setInternalState((current) => {
          if (current.to?.id !== value?.id) {
            return {
              ...current,
              to: value
            }
          } else {
            return current;
          }
        });
      }
    }
  }, [fields]);

  const onErrorEvent = useEffectEvent(onError);
  const handleValidating = useCallback((validating, dirty, errors) => {
    setIsDirty((current) => current || dirty || errors);
    onErrorEvent?.(errors);
  }, [onErrorEvent]);

  const onDeleteEvent = useEffectEvent(onDelete);
  const deleteAction = useMemo(() => ({
    label: 'Remove field',
    tooltip: 'Remove field',
    icon: Delete,
    ActionIconButtonProps: {
      IconProps: {
        size: 'smaller'
      }
    },
    IconButtonProps: {
      disabled: (disabled || readOnly || !removable || (only && isNew && !isDirty))
    },
    onClick: (e) => {
      onDeleteEvent?.(e);
    }
  }), [disabled, readOnly, removable, only, isDirty, isNew, onDeleteEvent]);

  const renderFormContent = useCallback((groups) => {
    const header = groups.find((g) => g.name === 'header');
    const body = groups.find((g) => g.name === 'body');

    const renderActions = () => {
      return <Box className="FieldMappingForm-actions">
        <ActionIconButton size="smaller"
                          density="sparse"
                          variant="contained"
                          color="error"
                          action={deleteAction}/>
      </Box>
    }

    return <React.Fragment>
      <Box className="FieldMappingForm-header">
        <Box className="Form-fields">
          {header.rendered}
        </Box>
        {!hideActions ? renderActions() : null}
      </Box>
      {body ? <Box className="FieldMappingForm-body">
        <Box className="Form-fields">
          {body.rendered}
        </Box>
      </Box> : null}
    </React.Fragment>
  }, [deleteAction, hideActions]);

  return <StyledFieldMappingForm ref={innerRef} {...innerProps}>
    <InlineForm ref={formRef}
                className="FieldMappingForm-form"
                fieldData={fieldData}
                autoFocus={isNew && !only}
                onSubmit={handleSubmit}
                onChange={handleChange}
                onError={handleError}
                onChangeDirect={handleChangeDirect}
                onValidating={handleValidating}
                renderContent={renderFormContent}
                fields={fields}/>
  </StyledFieldMappingForm>
});

FieldMappingForm.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  field: PropTypes.object,
  only: PropTypes.bool,
  list: PropTypes.object,
  team: PropTypes.object,
  connection: PropTypes.object,
  fieldData: PropTypes.object,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  removable: PropTypes.bool,
  hideActions: PropTypes.bool,
  error: PropTypes.bool,
  errorFrom: PropTypes.bool,
  errorTo: PropTypes.bool,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  onDelete: PropTypes.func
};

export default FieldMappingForm;
